import { Col, Form, FormInstance, Radio, Row } from 'antd';
import {
  BankAccountInfiniteSelectField,
  CheckboxGroupButtons,
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import { Typography } from 'antd';
import moment, { Moment } from 'moment';
import {
  CashFlowDateType,
  CashFlowSituation,
  CashFlowType,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';

export interface CashFlowChartFormValues {
  companyIds: number[];
  initialDate: Moment;
  type: CashFlowType;
  dateType: CashFlowDateType;
  situation: CashFlowSituation[];
  bankAccountIds: number[];
}
interface Props {
  form: FormInstance<CashFlowChartFormValues>;
}

function FinancialClashFlowForm({ form }: Props) {
  const formValues = Form.useWatch([], form);

  const chartTypeOptions = [
    {
      label: 'Semanal',
      value: CashFlowType.weekly,
    },
    {
      label: 'Período',
      value: CashFlowType.period,
    },
  ];

  const filterByDateOptions = [
    {
      label: 'Vencimento',
      value: CashFlowDateType.dueDate,
    },
    {
      label: 'Pagamento',
      value: CashFlowDateType.payDate,
    },
    {
      label: 'Emissão',
      value: CashFlowDateType.emissionDate,
    },
  ];

  const situationOptions = [
    {
      label: 'Abertos',
      value: CashFlowSituation.open,
    },
    {
      label: 'Liquidados',
      value: CashFlowSituation.settled,
    },
  ];

  return (
    <Form layout="vertical" form={form}>
      <Row gutter={24}>
        <Col span={24}>
          <FloatingFormItem label={'Empresas'} name="companyIds">
            <CompaniesInfiniteSelectField mode="multiple" allowClear />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>{'Tipo de gráfico'}</Typography.Title>
          <Form.Item
            name="type"
            initialValue={CashFlowType.weekly}
            getValueFromEvent={({ target }) => {
              if (target.value === CashFlowType.weekly) {
                form.setFieldValue('initialDate', moment().startOf('week'));
              } else {
                form.setFieldValue('initialDate', moment().startOf('month'));
              }

              return target.value;
            }}
          >
            <Radio.Group optionType="button" buttonStyle="solid" size="large">
              {chartTypeOptions.map((item) => (
                <Radio value={item.value}>{item.label}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="initialDate"
            label={'Data Inicial'}
            initialValue={moment().startOf('week')}
            getValueFromEvent={(value) => {
              if (formValues?.type === CashFlowType.weekly) {
                return moment(value).startOf('week');
              }

              return moment(value).startOf('month');
            }}
            rules={[
              { required: true, message: 'Informe uma data', type: 'date' },
            ]}
          >
            <InputDate
              picker={
                formValues?.type === CashFlowType.weekly ? 'week' : 'month'
              }
              allowClear={false}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>{'Filtrar por data de'}</Typography.Title>
          <Form.Item name="dateType" initialValue={CashFlowDateType.dueDate}>
            <Radio.Group optionType="button" buttonStyle="solid" size="large">
              {filterByDateOptions.map((item) => (
                <Radio value={item.value}>{item.label}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col span={24}>
          <Typography.Title level={5}>{'Situação'}</Typography.Title>
          <Form.Item
            name="situation"
            initialValue={[CashFlowSituation.open, CashFlowSituation.settled]}
          >
            <CheckboxGroupButtons options={situationOptions} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <FloatingFormItem label={'Contas Bancárias'} name="bankAccountIds">
            <BankAccountInfiniteSelectField
              mode="multiple"
              allowClear
              queryFilter={{ active: true }}
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default FinancialClashFlowForm;
